import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createBlock as _createBlock, Fragment as _Fragment, mergeProps as _mergeProps } from "vue";
const _hoisted_1 = {
  class: "flex items-center justify-between mb-4"
};
const _hoisted_2 = {
  key: 0,
  class: "mt-4"
};
const _hoisted_3 = {
  key: 1,
  class: "rounded-lg bg-gray-50 p-4"
};
const _hoisted_4 = {
  class: "flex items-center gap-2"
};
const _hoisted_5 = {
  class: "font-medium text-gray-900"
};
const _hoisted_6 = {
  class: "sr-only"
};
const _hoisted_7 = {
  key: 1
};
import { ref, computed, onMounted, reactive } from 'vue';
import { storeToRefs } from 'pinia';
import { useCustomFieldsStore } from './useCustomFieldsStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlTextField from '@/components/LlTextField.vue';
import LlSelect from '@/components/LlSelect.vue';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import LlIcon from '@/components/LlIcon.vue';
export default {
  __name: 'CustomFieldsTable',
  props: {
    state: {
      type: Object,
      required: false
    },
    alerts: {
      type: Object,
      required: false
    }
  },
  emits: ['edit', 'create'],
  setup(__props, {
    expose: __expose,
    emit: __emit
  }) {
    const props = __props;
    const emit = __emit;
    const customFieldsStore = useCustomFieldsStore();
    const {
      customFields,
      loading
    } = storeToRefs(customFieldsStore);
    const {
      handleError,
      handleSuccess
    } = useSnackbarHandler();

    // Search and filters
    const search = ref('');
    const entityTypeFilter = ref(null);
    const statusFilter = ref(null);

    // Delete dialog state
    const deleteDialog = reactive({
      show: false,
      customField: null,
      title: '',
      message: ''
    });

    // Table headers
    const headers = [{
      title: 'Name',
      key: 'name',
      sortable: true
    }, {
      title: 'Associated with',
      key: 'entityType',
      sortable: true
    }, {
      title: 'Field Type',
      key: 'fieldType',
      sortable: true
    }, {
      title: 'Include on Invoice',
      key: 'includeOnInvoice',
      sortable: true,
      align: 'center'
    }, {
      title: 'Actions',
      key: 'actions',
      sortable: false,
      align: 'center'
    }];

    // Helper functions to handle API field name variations
    const getEntityType = item => {
      // Backend returns boolean flags for entity types
      if (item.showOnClient) return 'client';
      if (item.showOnMatter) return 'matter';
      if (item.showOnAccount) return 'account';
      if (item.showOnCharge) return 'charge';
      return null;
    };
    const getFieldType = item => {
      // Backend returns 'type' field with values like 'text', 'enum', 'number'
      return item.type;
    };
    const formatEntityType = type => {
      const types = {
        client: 'Client',
        matter: 'Matter',
        account: 'User',
        charge: 'Fixed Fee'
      };
      return types[type] || type;
    };
    const formatFieldType = type => {
      const types = {
        text: 'Text',
        number: 'Number',
        enum: 'Dropdown',
        dropdown: 'Dropdown',
        date: 'Date'
      };
      return types[type] || type;
    };

    // Actions
    const handleCreateNew = () => {
      emit('create');
    };
    const handleEdit = customField => {
      emit('edit', customField);
    };
    const handleDelete = async customField => {
      // Load usage information to show in warning
      try {
        const usageData = await customFieldsStore.getCustomFieldUsage(customField.id);
        const usageParts = [];
        if (usageData.totalClients > 0) {
          usageParts.push(`${usageData.totalClients} client${usageData.totalClients > 1 ? 's' : ''}`);
        }
        if (usageData.totalMatters > 0) {
          usageParts.push(`${usageData.totalMatters} matter${usageData.totalMatters > 1 ? 's' : ''}`);
        }
        if (usageData.totalUsers > 0 || usageData.totalAccounts > 0) {
          const count = usageData.totalUsers || usageData.totalAccounts;
          usageParts.push(`${count} user${count > 1 ? 's' : ''}`);
        }
        if (usageData.totalCharges > 0) {
          usageParts.push(`${usageData.totalCharges} fixed fee${usageData.totalCharges > 1 ? 's' : ''}`);
        }
        deleteDialog.customField = customField;
        deleteDialog.title = `Delete "${customField.name}"?`;
        if (usageParts.length > 0) {
          deleteDialog.message = `This custom field is currently associated with ${usageParts.join(', ')}. Deleting it will remove this data from all associated records. This action cannot be undone. Are you sure you want to continue?`;
        } else {
          deleteDialog.message = 'Are you sure you want to delete this custom field? This action cannot be undone.';
        }
        deleteDialog.show = true;
      } catch (error) {
        // If we can't get usage, still allow deletion with generic warning
        deleteDialog.customField = customField;
        deleteDialog.title = `Delete "${customField.name}"?`;
        deleteDialog.message = 'Are you sure you want to delete this custom field? This may affect associated records. This action cannot be undone.';
        deleteDialog.show = true;
      }
    };
    const confirmDelete = async () => {
      try {
        await customFieldsStore.deleteCustomField(deleteDialog.customField.id);
        handleSuccess('Custom field deleted successfully');
        deleteDialog.show = false;
        deleteDialog.customField = null;
      } catch (error) {
        handleError(error);
      }
    };
    const cancelDelete = () => {
      deleteDialog.show = false;
      deleteDialog.customField = null;
    };

    // Load custom fields
    const loadCustomFields = async () => {
      try {
        await customFieldsStore.listCustomFields();
      } catch (error) {
        handleError(error);
      }
    };

    // Expose method for parent component
    __expose({
      loadCustomFields
    });
    onMounted(() => {
      loadCustomFields();
    });
    return (_ctx, _cache) => {
      const _component_v_skeleton_loader = _resolveComponent("v-skeleton-loader");
      const _component_v_chip = _resolveComponent("v-chip");
      const _component_v_list_item_title = _resolveComponent("v-list-item-title");
      const _component_v_list_item = _resolveComponent("v-list-item");
      const _component_v_list = _resolveComponent("v-list");
      const _component_v_menu = _resolveComponent("v-menu");
      const _component_v_data_table = _resolveComponent("v-data-table");
      return _openBlock(), _createElementBlock("div", null, [_createElementVNode("div", _hoisted_1, [_cache[2] || (_cache[2] = _createElementVNode("div", null, [_createElementVNode("h1", {
        class: "text-2xl font-semibold text-gray-900"
      }, "Custom Fields"), _createElementVNode("p", {
        class: "text-sm text-gray-600 mt-1"
      }, " Create custom fields to track additional information for clients, matters, and users ")], -1)), _createVNode(LlSecondaryButton, {
        color: "primary",
        "prepend-icon": "mdi-plus",
        onClick: handleCreateNew,
        "data-cy": "create-custom-field-btn"
      }, {
        default: _withCtx(() => [...(_cache[1] || (_cache[1] = [_createTextVNode(" Create Custom Field ", -1)]))]),
        _: 1
      })]), _unref(loading) ? (_openBlock(), _createElementBlock("div", _hoisted_2, [_createVNode(_component_v_skeleton_loader, {
        type: "table-row-divider@5"
      })])) : (_openBlock(), _createElementBlock("div", _hoisted_3, [_createVNode(_component_v_data_table, {
        headers: headers,
        items: _unref(customFields),
        "items-per-page": 20,
        class: "!bg-[rgb(var(--v-theme-surface))] rounded",
        "data-cy": "custom-fields-table"
      }, {
        [`item.name`]: _withCtx(({
          item
        }) => [_createElementVNode("div", _hoisted_4, [_createElementVNode("span", _hoisted_5, _toDisplayString(item.name), 1), !item.active ? (_openBlock(), _createBlock(_component_v_chip, {
          key: 0,
          size: "x-small",
          color: "grey",
          variant: "flat"
        }, {
          default: _withCtx(() => [...(_cache[3] || (_cache[3] = [_createTextVNode(" Inactive ", -1)]))]),
          _: 1
        })) : _createCommentVNode("", true)])]),
        [`item.entityType`]: _withCtx(({
          item
        }) => [_createVNode(_component_v_chip, {
          size: "small",
          variant: "flat",
          class: "bg-gray-100 text-gray-900"
        }, {
          default: _withCtx(() => [_createTextVNode(_toDisplayString(formatEntityType(getEntityType(item))), 1)]),
          _: 2
        }, 1024)]),
        [`item.fieldType`]: _withCtx(({
          item
        }) => [_createVNode(_component_v_chip, {
          size: "small",
          variant: "flat",
          class: "bg-gray-100 text-gray-900"
        }, {
          default: _withCtx(() => [_createTextVNode(_toDisplayString(formatFieldType(getFieldType(item))), 1)]),
          _: 2
        }, 1024)]),
        [`item.includeOnInvoice`]: _withCtx(({
          item
        }) => [item.entity === 'client' || item.entity === 'matter' ? (_openBlock(), _createElementBlock(_Fragment, {
          key: 0
        }, [_createVNode(LlIcon, {
          icon: item.showOnInvoice ? 'mdi-check-circle' : 'mdi-minus-circle',
          color: item.showOnInvoice ? 'primary' : 'grey-lighten-1'
        }, null, 8, ["icon", "color"]), _createElementVNode("span", _hoisted_6, _toDisplayString(item.showOnInvoice ? 'Yes' : 'No'), 1)], 64)) : (_openBlock(), _createElementBlock("span", _hoisted_7, "-"))]),
        [`item.actions`]: _withCtx(({
          item
        }) => [_createVNode(_component_v_menu, null, {
          activator: _withCtx(({
            props: menuProps
          }) => [_createVNode(LlSecondaryButton, _mergeProps({
            icon: "mdi-dots-vertical",
            variant: "text",
            size: "small",
            "aria-label": `${item.name} actions`
          }, menuProps, {
            density: "comfortable",
            "data-cy": "custom-field-actions-btn"
          }), null, 16, ["aria-label"])]),
          default: _withCtx(() => [_createVNode(_component_v_list, {
            density: "compact"
          }, {
            default: _withCtx(() => [_createVNode(_component_v_list_item, {
              onClick: $event => handleEdit(item),
              "data-cy": "edit-custom-field-btn"
            }, {
              default: _withCtx(() => [_createVNode(_component_v_list_item_title, null, {
                default: _withCtx(() => [...(_cache[4] || (_cache[4] = [_createTextVNode("Edit", -1)]))]),
                _: 1
              })]),
              _: 1
            }, 8, ["onClick"]), _createVNode(_component_v_list_item, {
              onClick: $event => handleDelete(item),
              class: "text-error",
              "data-cy": "delete-custom-field-btn"
            }, {
              default: _withCtx(() => [_createVNode(_component_v_list_item_title, null, {
                default: _withCtx(() => [...(_cache[5] || (_cache[5] = [_createTextVNode("Delete", -1)]))]),
                _: 1
              })]),
              _: 1
            }, 8, ["onClick"])]),
            _: 2
          }, 1024)]),
          _: 2
        }, 1024)]),
        _: 2
      }, 1032, ["items"])])), _createVNode(LlConfirmationDialog, {
        modelValue: deleteDialog.show,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => deleteDialog.show = $event),
        title: deleteDialog.title,
        message: deleteDialog.message,
        confirmText: "Delete",
        confirmColor: "error",
        onConfirm: confirmDelete,
        onCancel: cancelDelete
      }, null, 8, ["modelValue", "title", "message"])]);
    };
  }
};